import { AVAILABLE_WORLDS } from '../../languages/constants';
import { Devices } from '../devices';
import { ADAPT_DESTINATION_ARMS } from './adapt-cta-destination';

export type ABTestGroupName = keyof typeof AB_TEST_GROUPS;
export type ABTestKey = keyof typeof AB_TESTS;
export type StatsigABTestKey = keyof typeof STATSIG_AB_TESTS;
export type ABTestProps = {
    gaDimension: string;
    // String type uses for backward compatibility. In the future in ideal world this prop should have type only from AB_TESTS keys
    gaTestName: ABTestKey | string;
    unleashKey: string;
    hardApplyParam?: string;
};
export type ABTestData = ABTestProps & {
    groupActiveTest?: ABTestKey | '';
};
export type StatsigABTestDataType = {
    statsigKey: string;
    // Present ⇒ read from a layer (getLayer(layerName).get(statsigKey)) instead of getExperiment
    layerName?: string;
    // Present ⇒ read this parameter instead of the one named like statsigKey.
    // The ?<statsigKey>=<value> QA override keeps using statsigKey, never parameterName.
    parameterName?: string;
    // Present ⇒ these values count as assigned even when Statsig reports the user outside the running experiment, as it
    // can for a user placed by a conditional override or while the experiment is not started. While it runs, the events'
    // ab_tests report these values under statsigKey too. Never list the value users outside the experiment read (the
    // code fallback): every other user gets that one
    overrideAssignedValues?: string[];
    limitedWorlds?: AVAILABLE_WORLDS | AVAILABLE_WORLDS[];
    allowedDevices?: Devices[];
};

type ABTestGroups = { [key in ABTestGroupName]: Array<ABTestKey> };
type ABTests = { [key in ABTestKey]: ABTestProps };
type StatsigABTests = { [key in StatsigABTestKey]: StatsigABTestDataType };

enum AB_TEST_GROUPS {
    MaterialPageDesktopABTestsGroup = 'Group for AB tests on the material page for the desktop view',
    MaterialPageDesktopABTestsGroup2 = 'The 2nd group for AB tests on the material page for the desktop view',
    MaterialPageMobileABTestsGroup = 'Group for AB tests on the material page for the mobile view',
    MaterialPageABTestsGroup = 'Group for AB tests on the material page for all platforms'
}

enum AB_TESTS {}

export const EXISTED_AB_TEST_GROUPS: ABTestGroups = {
    MaterialPageDesktopABTestsGroup: [],
    MaterialPageDesktopABTestsGroup2: [],
    MaterialPageMobileABTestsGroup: [],
    MaterialPageABTestsGroup: []
};

export const EXISTED_AB_TESTS: ABTests = {};

enum STATSIG_AB_TESTS {
    polotnoEditorTest = 'Tesing new Worksheet based on some canvas editor tool called Polotno',
    aiMaterialCustomizer = 'Testing of the new Eduki_Material_Customizer tool for a selected group of test users',
    myMaterialBundleDisplay = 'My material bundle display',
    searchListHighlight = 'Highlight search query matches in list-view title/description for DACH web + tablet',
    aiStudioRollout = 'Replace legacy AI-Tools + Customizer entry points with the new AI Studio workspace (Phase-1 internal prod A/B)',
    aiWorksheetCreator = 'Testing of the new Complementary_worksheet tool for a selected group of test users',
    urgencySignal = 'Show urgency signal on PDP for materials with 10+ purchases in last 30 days',
    adaptAiToolExperiment = 'Backdoor AB test for AI tool adaptation experiment',
    adaptOctDestination = 'October cohort: send the eduki Adapt CTAs to the Adapt landing page or straight into eduki Adapt',
    pickedForYou = 'Show a "Picked for you" personalized recommendations row above the thematic section on PDP for eligible users',
    searchFilterPersistence = 'Keep active search filters when the search query changes',
    searchLTRv14 = 'Search ranking: test_segment 36 (LTR_1_3) vs test_segment 43 (LTR_1_4)'
}

export const EXISTED_STATSIG_AB_TESTS: StatsigABTests = {
    polotnoEditorTest: {
        statsigKey: 'polotno_editor_test',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    aiMaterialCustomizer: {
        statsigKey: 'ai_material_customizer',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    myMaterialBundleDisplay: {
        statsigKey: 'my_material_bundle_display',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    searchListHighlight: {
        statsigKey: 'highlight_search_keyword',
        limitedWorlds: AVAILABLE_WORLDS.DE,
        allowedDevices: ['desktop', 'tablet']
    },
    aiStudioRollout: {
        statsigKey: 'ai_studio_rollout',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    aiWorksheetCreator: {
        statsigKey: 'ai_worksheet_creator',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - 1406 (remove on either accept or reject) — isolated in material-page layer
    urgencySignal: {
        statsigKey: 'urgency_signal_isolated',
        layerName: 'material-page',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - MCP-1473 (remove on either accept or reject)
    // It still decides who outside the October cohort sees the Adapt banner and order confirmation modal (and with
    // which copy), and its variant rides on the landing link. The cohort sees the launch edition without it
    adaptAiToolExperiment: {
        statsigKey: 'adapt_ai_tool_experiment',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - AI-1771 / AI-1774 — read by the My Materials banner, the thank-you page banner and the order
    // confirmation modal (hooks/ab-tests/use-adapt-entry-point documents when). Both arms are the October cohort,
    // who see the launch edition whether or not they are in MCP-1473.
    // AB TEST - AI-1775 — also read on the KI Tools page (routes/ai-tools/hooks/use-ai-tools-adapt-banner), where the
    // cohort gets the eduki Adapt banner, and by the header's Customiser waitlist bar, which the cohort no longer sees.
    // Statsig side: string parameter `destination`, 'landing' | 'tool', on the hashed user ID
    // (utils/user/hash-user-id). Conditional overrides put the segments adapt_oct_arm_landing and adapt_oct_arm_tool in
    // their groups; allocation is 100%, but targeting admits only those two segments, so every other teacher stays
    // outside and reads the code fallback '', which keeps each entry point's link from before the experiment. Keep the
    // targeting on the two segments: without it, 100% allocation would split every DE teacher between the arms. The
    // fallback must be neither arm, because overrideAssignedValues would count it as assigned.
    // QA: ?adapt_oct_destination=landing|tool forces an arm (signed in, DE world).
    // Every 'tool' ID must also pass the eduki_adapt_access_v1 gate, the only check the Studio enforces: anyone else
    // is bounced from the Studio to /de/adapt after a sign-in round-trip, with no variant or user_id to attribute the
    // visit. Both segments are rules of that gate, so both arms pass it.
    adaptOctDestination: {
        statsigKey: 'adapt_oct_destination',
        parameterName: 'destination',
        overrideAssignedValues: [ADAPT_DESTINATION_ARMS.LANDING, ADAPT_DESTINATION_ARMS.TOOL],
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - 1374 (remove on either accept or reject) — isolated in material-page layer
    pickedForYou: {
        statsigKey: 'picked_for_you_isolated',
        layerName: 'material-page',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    // AB TEST - SRCH-1552 (remove on either accept or reject)
    searchFilterPersistence: {
        statsigKey: 'search_filter_persistence',
        layerName: 'search',
        limitedWorlds: AVAILABLE_WORLDS.DE
    },
    searchLTRv14: {
        statsigKey: 'LTR_1_4',
        layerName: 'search',
        limitedWorlds: AVAILABLE_WORLDS.DE
    }
};
